/**
 * Top-level routing for the Inspection Report Dashboard.
 *
 * HashRouter mirrors the inspector PWA so we don't need a backend rewrite
 * rule for nested paths. Auth gate is identical: any route except /login
 * redirects to /login when no session is in localStorage.
 */

import { useEffect, useState } from 'react';
import { HashRouter, Navigate, NavLink, Route, Routes, useNavigate, Link } from 'react-router-dom';
import { canAccessDashboardArea, type DashboardArea } from '@cer-autofill/shared';
import { clearSession, getSession, setDashboardProfile, type AdminSession } from './session.js';
import { fetchDashboardSessionProfile } from './auth.js';
import { keepSessionFresh, sessionWithFreshToken } from './api.js';
import { LoginScreen } from './routes/LoginScreen.js';
import { ReportsListScreen } from './routes/ReportsListScreen.js';
import { BrandMark } from './components/BrandMark.js';
import { ReportDetailScreen } from './routes/ReportDetailScreen.js';
import { JobsListScreen } from './routes/JobsListScreen.js';
import { JobDetailScreen } from './routes/JobDetailScreen.js';
import { AdminScreen } from './routes/AdminScreen.js';
import { InvoicingScreen } from './routes/InvoicingScreen.js';
import { SchedulingScreen } from './routes/SchedulingScreen.js';
import { AvailabilityScreen } from './routes/AvailabilityScreen.js';
import { SettingsScreen } from './routes/SettingsScreen.js';
import { reportReauthRequired, reportSessionCleared } from './lib/auth-diag.js';

export function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route
          path="/"
          element={
            <AuthGate>
              <Shell area="jobs">
                <JobsListScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/reports"
          element={
            <AuthGate>
              <Shell area="reports">
                <ReportsListScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/reports/:jobId/:submittedAt"
          element={
            <AuthGate>
              <Shell area="reports">
                <ReportDetailScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/jobs"
          element={
            <AuthGate>
              <Shell area="jobs">
                <JobsListScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/scheduling"
          element={
            <AuthGate>
              <Shell area="bookings">
                <SchedulingScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/availability"
          element={
            <AuthGate>
              <Shell area="availability">
                <AvailabilityScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/invoicing"
          element={
            <AuthGate>
              <Shell area="invoicing">
                <InvoicingScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/jobs/:jobId"
          element={
            <AuthGate>
              <Shell area="jobs">
                <JobDetailScreen />
              </Shell>
            </AuthGate>
          }
        />
        {/* TODO(2026-11-03): remove after bookmarked pre-refactor URLs age out. */}
        <Route
          path="/submissions/:jobId/:submittedAt"
          element={
            <AuthGate>
              <Shell area="reports">
                <ReportDetailScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/cases"
          element={
            <AuthGate>
              <Shell area="jobs">
                <JobsListScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/cases/:jobId"
          element={
            <AuthGate>
              <Shell area="jobs">
                <JobDetailScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/admin"
          element={
            <AuthGate>
              <Shell area="admin">
                <AdminScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGate>
              <Shell area="availability">
                <SettingsScreen />
              </Shell>
            </AuthGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'authed' | 'unauthed'>('loading');

  useEffect(() => {
    void loadDashboardSession().then((session) => {
      if (session) {
        setState('authed');
      } else {
        setState('unauthed');
        reportReauthRequired('no_session_on_boot');
        void navigate('/login', { replace: true });
      }
    });
  }, [navigate]);

  if (state === 'loading') {
    return (
      <div className="screen screen--center">
        <p className="loading">Loading…</p>
      </div>
    );
  }
  if (state === 'unauthed') return null;
  return <>{children}</>;
}

async function loadDashboardSession(): Promise<AdminSession | null> {
  try {
    const session = await sessionWithFreshToken();
    if (!session) return null;
    if (session.source === 'legacy') return session;
    if (!session.idToken) return null;
    return setDashboardProfile(await fetchDashboardSessionProfile(session.idToken));
  } catch {
    // sessionWithFreshToken already reports refresh_failed when that is why
    // we are here; AuthGate still reports no_session_on_boot for a cold empty
    // store. Returning null keeps user-facing behaviour unchanged.
    return null;
  }
}

function Shell({ children, area }: { children: React.ReactNode; area: DashboardArea }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<AdminSession | null>(() => getSession());

  useEffect(() => keepSessionFresh(), []);

  function onLogout() {
    reportSessionCleared('explicit_logout');
    clearSession();
    setSession(null);
    void navigate('/login', { replace: true });
  }

  if (!session || !canAccessDashboardArea(session.roles, area)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <header className="app-header">
        <Link to="/" className="app-header__brand-link" aria-label="Home">
          <BrandMark compact />
        </Link>
        <nav className="app-header__nav">
          {canAccessDashboardArea(session.roles, 'jobs') && (
            <NavLink
              to="/"
              end
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Jobs
            </NavLink>
          )}
          {canAccessDashboardArea(session.roles, 'availability') && (
            <NavLink
              to="/availability"
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Availability
            </NavLink>
          )}
          {canAccessDashboardArea(session.roles, 'bookings') && (
            <NavLink
              to="/scheduling"
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Bookings
            </NavLink>
          )}
          {canAccessDashboardArea(session.roles, 'reports') && (
            <NavLink
              to="/reports"
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Reports
            </NavLink>
          )}
          {canAccessDashboardArea(session.roles, 'invoicing') && (
            <NavLink
              to="/invoicing"
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Invoicing
            </NavLink>
          )}
          {canAccessDashboardArea(session.roles, 'admin') && (
            <NavLink
              to="/admin"
              className={({ isActive }: { isActive: boolean }) =>
                `app-header__nav-link ${isActive ? 'app-header__nav-link--active' : ''}`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>
        <div className="app-header__spacer" />
        {session && (
          <span className="app-header__who">
            Signed in as {session.name}
            {session.source === 'legacy' ? ' (legacy)' : ''}
          </span>
        )}
        {session && canAccessDashboardArea(session.roles, 'availability') && (
          <NavLink
            to="/settings"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__icon-link ${isActive ? 'app-header__icon-link--active' : ''}`
            }
            aria-label="Settings"
            title="Settings"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M19.14 12.94a7.6 7.6 0 0 0 0-1.88l2-1.55a.5.5 0 0 0 .12-.62l-1.9-3.28a.5.5 0 0 0-.6-.22l-2.35.94a7.3 7.3 0 0 0-1.63-.94l-.36-2.5a.5.5 0 0 0-.5-.42h-3.8a.5.5 0 0 0-.49.42l-.36 2.5c-.59.24-1.13.56-1.63.94l-2.35-.94a.5.5 0 0 0-.6.22L1.8 8.89a.5.5 0 0 0 .12.62l2 1.55a7.6 7.6 0 0 0 0 1.88l-2 1.55a.5.5 0 0 0-.12.62l1.9 3.28c.13.22.39.3.6.22l2.35-.94c.5.38 1.04.7 1.63.94l.36 2.5c.04.24.25.42.5.42h3.8c.25 0 .46-.18.5-.42l.36-2.5c.58-.24 1.13-.56 1.63-.94l2.35.94c.21.08.47 0 .6-.22l1.9-3.28a.5.5 0 0 0-.12-.62ZM12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5"
              />
            </svg>
          </NavLink>
        )}
        {session && (
          <button type="button" className="app-header__logout" onClick={onLogout}>
            Sign out
          </button>
        )}
      </header>
      {children}
    </>
  );
}
